import { useState, useRef, useCallback, useEffect } from "react";

import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import { PieChart } from "@mui/x-charts/PieChart";
import { BarChart } from "@mui/x-charts/BarChart";
import { useDrawingArea } from "@mui/x-charts/hooks";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { renderAlarm, renderWarning } from "../../components/StatusChip";
import {getEventStatus} from "../../api/eventStatusAPI";
import { getNode } from "../../api/nodeAPI";
import { getProjectDashboardObjectCodes } from "../../api/projectAPI";
import { useObjectData } from "../../hooks/useLocalStorageData";
import { useRefreshInterval as useRefreshIntervalHook } from "../../hooks/useInterval";
import { useObjectMaps } from "../../hooks/useObjectMaps";
import { getCurrentTenant } from "../../config/tenantConfig";

const StyledText = styled("text", {
  shouldForwardProp: (prop) => prop !== "variant",
})(({ theme }) => ({
  textAnchor: "middle",
  dominantBaseline: "central",
  fill: (theme.vars || theme).palette.text.secondary,
  variants: [
    {
      props: { variant: "primary" },
      style: {
        fontSize: theme.typography.h5.fontSize,
        fontWeight: theme.typography.h5.fontWeight,
      },
    },
    {
      props: ({ variant }) => variant !== "primary",
      style: {
        fontSize: theme.typography.body2.fontSize,
        fontWeight: theme.typography.body2.fontWeight,
      },
    },
  ],
}));

function PieCenterLabel({ primaryText, secondaryText }) {
  const { width, height, left, top } = useDrawingArea();
  const primaryY = top + height / 2 - 10;
  const secondaryY = primaryY + 24;
  return (
    <>
      <StyledText variant="primary" x={left + width / 2} y={primaryY}>
        {primaryText}
      </StyledText>
      <StyledText variant="secondary" x={left + width / 2} y={secondaryY}>
        {secondaryText}
      </StyledText>
    </>
  );
}

export default function MiniDataGrid() {
  const [rows, setRows] = useState([]);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogNodes, setDialogNodes] = useState([]);
  const [dialogAlarmType, setDialogAlarmType] = useState(2);
  const [dialogStatusDist, setDialogStatusDist] = useState([]);
  const [dialogEpochData, setDialogEpochData] = useState({ labels: [], data: [] });
  const inFlightRef = useRef(false);
  const rawDataRef = useRef([]);
  const nodeMapRef = useRef(new Map());
  // Once rows have been rendered at least once we stop re-painting the 0-count
  // skeleton on every refresh (avoids the counts flashing back to 0).
  const hasRowsRef = useRef(false);
  const objectData = useObjectData();
  const tenant = getCurrentTenant();
  const isSarj = tenant.localeOverride === "SARJ";
  const isRcu = tenant.projectCode === "RCU";
  const isVendai = tenant.projectCode === "VENDAI";
  const [rcuDashboardCodes, setRcuDashboardCodes] = useState([]);

  // Use centralized object maps hook
  const { nameMap: objectNameMap, descMap: objectDescMap } = useObjectMaps(objectData);

  // RCU: dashboard rows are driven by the project's projectDashboardJSon object codes.
  useEffect(() => {
    if (!isRcu) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const codes = await getProjectDashboardObjectCodes();
        if (!cancelled) setRcuDashboardCodes(Array.isArray(codes) ? codes : []);
      } catch (err) {
        console.error("Error loading RCU dashboard codes:", err);
        if (!cancelled) setRcuDashboardCodes([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isRcu]);

  const fetchData = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setError(null);
    setLoading(true);
    try {
      const DASHBOARD_EVENTS = [
        "Otomat Durumu",
        ...(isSarj ? ["Şarj İstasyonu Durumu"] : []),
        "POS Ünitesi",
        "Nakit Üniteleri",
        "Satış Durumu",
        "Sıcaklık",
        "Sinyal Düzeyi",
        "Stok Seviyesi",
        "Kapı Durumu",
      ];
      // RCU: rows come from the project's dashboard object codes (order preserved);
      // other tenants use the fixed event-name list.
      const dashboardObjects = isRcu
        ? rcuDashboardCodes
            .map((code) => objectData.find((o) => o.code === code))
            .filter(Boolean)
        : DASHBOARD_EVENTS
            .map((name) => objectData.find((o) => o.name === name))
            .filter(Boolean);
      const allowedEventIds = new Set(dashboardObjects.map((obj) => obj.code));
      // Paint the row list immediately (event names with 0 counts) so the grid
      // is not blocked by the slow getEventStatus()/getNode() fetch below.
      // Only on first paint — later refreshes keep the existing counts visible.
      if (!hasRowsRef.current && dashboardObjects.length > 0) {
        setRows(
          dashboardObjects.map((obj) => ({
            id: obj.code,
            eventName: obj.name,
            error: 0,
            warning: 0,
          }))
        );
      }
      const [fullList, allNodes] = await Promise.all([
        getEventStatus(),
        getNode(),
      ]);
      nodeMapRef.current = new Map(allNodes.map((n) => [n.idNode, n]));
      const filteredList = fullList.filter((item) => allowedEventIds.has(item.event));
      rawDataRef.current = filteredList;
      // Initialize all dashboard events with 0 counts
      const eventCounts = {};
      dashboardObjects.forEach((obj) => {
        eventCounts[obj.code] = {
          event: obj.name,
          warning: 0,
          alarm: 0,
        };
      });
      // Overlay actual data
      filteredList.forEach((item) => {
        const eventId = item.event;
        const alarmType = item.alarmType;
        if (!eventCounts[eventId]) {
          eventCounts[eventId] = {
            event: objectNameMap.get(eventId.toString()) || eventId,
            warning: 0,
            alarm: 0,
          };
        }
        if (alarmType === 1) {
          eventCounts[eventId].warning += 1;
        } else if (alarmType === 2) {
          eventCounts[eventId].alarm += 1;
        }
      });
      const rowsData = dashboardObjects.map((obj) => {
        const counts = eventCounts[obj.code];
        return {
          id: obj.code,
          eventName: counts?.event || obj.name,
          error: counts?.alarm || 0,
          warning: counts?.warning || 0,
        };
      });
      setRows(rowsData);
      hasRowsRef.current = true;
    } catch (err) {
      setError(err.message || "Veri alınamadı");
    } finally {
      setLoading(false);
      inFlightRef.current = false;
    }
  }, [objectNameMap, isSarj, isRcu, rcuDashboardCodes]);

  // Use refresh interval hook (handles interval changes and refresh all events)
  useRefreshIntervalHook(fetchData, {
    defaultInterval: 60,
    storageKey: 'refreshInterval',
    // Re-fetch once the RCU project dashboard codes have loaded (async).
    dependencies: [rcuDashboardCodes],
  });

  const handleCountClick = useCallback((eventId, alarmType) => {
    const items = rawDataRef.current.filter(
      (item) => String(item.event) === String(eventId) && item.alarmType === alarmType
    );
    const nodeCountMap = new Map();
    items.forEach((item) => {
      const rawEpoch = Number(item.epoch);
      const epoch = Number.isFinite(rawEpoch) ? rawEpoch : null;
      const existing = nodeCountMap.get(item.nodeId);
      if (existing) {
        existing.count += 1;
        if (epoch != null && (existing.lastEpoch == null || epoch > existing.lastEpoch)) {
          existing.lastEpoch = epoch;
        }
      } else {
        const node = nodeMapRef.current.get(item.nodeId);
        nodeCountMap.set(item.nodeId, {
          name: node?.name || `Node ${item.nodeId}`,
          idNode: item.nodeId,
          count: 1,
          lastEpoch: epoch,
        });
      }
    });
    const groupedNodes = Array.from(nodeCountMap.values())
      .sort((a, b) => b.count - a.count);

    // Status distribution for donut (normal vs warning vs alarm)
    const allEventItems = rawDataRef.current.filter(
      (item) => String(item.event) === String(eventId)
    );
    const normalCount = allEventItems.filter((i) => i.alarmType === 0).length;
    const warningCount = allEventItems.filter((i) => i.alarmType === 1).length;
    const alarmCount = allEventItems.filter((i) => i.alarmType === 2).length;
    setDialogStatusDist(
      [
        { id: 0, label: t("dashboard.topicHeader.normal"), value: normalCount, color: "#4caf50" },
        { id: 1, label: t("places.dataGrid.warning"), value: warningCount, color: "#f57c00" },
        { id: 2, label: t("places.dataGrid.error"), value: alarmCount, color: "#c62828" },
      ].filter((d) => d.value > 0)
    );

    // Epoch-based timeline
    const epochItems = items
      .filter((i) => i.epoch != null)
      .sort((a, b) => a.epoch - b.epoch);
    const dayMap = new Map();
    epochItems.forEach((item) => {
      let epoch = Number(item.epoch);
      if (epoch < 1e12) epoch *= 1000;
      const date = new Date(epoch);
      const dayKey = `${date.getDate().toString().padStart(2, "0")}.${(date.getMonth() + 1)
        .toString()
        .padStart(2, "0")}`;
      dayMap.set(dayKey, (dayMap.get(dayKey) || 0) + 1);
    });
    setDialogEpochData({
      labels: Array.from(dayMap.keys()),
      data: Array.from(dayMap.values()),
    });

    const row = rows.find((r) => String(r.id) === String(eventId));
    const eventName = row?.eventName || eventId;
    const typeLabel = alarmType === 1 ? t("places.dataGrid.warning") : t("places.dataGrid.error");
    setDialogTitle(`${eventName} - ${typeLabel}`);
    setDialogNodes(groupedNodes);
    setDialogAlarmType(alarmType);
    setDialogOpen(true);
  }, [rows, t]);

  const columns = [
    {
      field: "eventName",
      headerName: t("alarmHistory.eventName"),
      width: 200,
      flex: 2,
      renderHeader: () => null,
      renderCell: (params) => {
        const idKey = params.row?.id != null ? params.row.id.toString() : "";
        const rawTooltip = objectDescMap.get(idKey) ?? "-";
        const tooltipText = isSarj
          ? rawTooltip.replace(/Otomat Durumu/g, "Şarj İstasyonu Durumu")
          : rawTooltip;
        return (
          <Tooltip
            arrow
            disableInteractive
            title={
              <Box sx={{ maxWidth: 520 }}>
                <Box
                  sx={{ whiteSpace: "pre-wrap", fontSize: 14, lineHeight: 1.5 }}
                >
                  {tooltipText}
                </Box>
              </Box>
            }
            componentsProps={{
              tooltip: {
                sx: (theme) => ({
                  maxWidth: 560,
                  p: 1.5,
                  fontSize: 14,
                  bgcolor:
                    theme.palette.mode === "dark" ? "#ffffff" : "#000000",
                  color: theme.palette.mode === "dark" ? "#000000" : "#ffffff",
                  boxShadow: 4,
                  borderRadius: 1,
                  border: `1px solid ${
                    theme.palette.mode === "dark" ? "#e5e7eb" : "#333333"
                  }`,
                }),
              },
              arrow: {
                sx: (theme) => ({
                  color: theme.palette.mode === "dark" ? "#ffffff" : "#000000",
                }),
              },
            }}
          >
            <Box
              component="span"
              sx={{
                transition: "color 0.15s ease",
                "&:hover": (theme) => ({
                  textDecoration: "underline",
                  textDecorationColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  cursor: "pointer",
                }),
              }}
              onClick={() => {
                const row = params.row;
                const hasIssues = row.error > 0 || row.warning > 0;
                const listPath = isRcu ? "places" : "vending-machines";
                navigate(hasIssues ? `/${listPath}?event=${row.id}` : `/${listPath}`);
              }}
            >
              {params.value}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "error",
      headerName: t("places.dataGrid.error"),
      flex: 1,
      minWidth: 60,
      renderCell: (params) => (
        <Box
          onClick={(e) => {
            if (params.value > 0) {
              e.stopPropagation();
              handleCountClick(params.row.id, 2);
            }
          }}
          sx={{
            cursor: params.value > 0 ? "pointer" : "default",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: isVendai ? "flex-start" : "center",
          }}
        >
          {renderAlarm(params.value)}
        </Box>
      ),
    },
    {
      field: "warning",
      headerName: t("places.dataGrid.warning"),
      flex: 1,
      minWidth: 60,
      renderCell: (params) => (
        <Box
          onClick={(e) => {
            if (params.value > 0) {
              e.stopPropagation();
              handleCountClick(params.row.id, 1);
            }
          }}
          sx={{
            cursor: params.value > 0 ? "pointer" : "default",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: isVendai ? "flex-start" : "center",
          }}
        >
          {renderWarning(params.value)}
        </Box>
      ),
    },
  ];

  return (
    <div style={{ position: "relative" }}>
      <DataGrid
        rows={rows}
        columns={columns}
        rowHeight={44}
        headerHeight={48}
        density="standard"
        hideFooter
        loading={loading}
        sx={{
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
            display: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            msOverflowStyle: "none",
            scrollbarWidth: "none",
          },
          cursor: "pointer",
          borderRadius: 0,
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
            display: "flex",
            alignItems: "center",
          },
          "& .MuiDataGrid-columnSeparator": {
            visibility: "hidden",
          },
        }}
      />
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
          {dialogTitle}
          <IconButton onClick={() => setDialogOpen(false)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          {dialogNodes.length > 0 && (
            <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", justifyContent: "center" }}>
              {/* Donut Chart - Status Distribution */}
              {dialogStatusDist.length > 0 && (
                <Box sx={{ flex: "0 0 auto" }}>
                  <PieChart
                    skipAnimation
                    margin={{ left: 10, right: 10, top: 10, bottom: 10 }}
                    tooltip={{ trigger: "none" }}
                    series={[
                      {
                        data: dialogStatusDist,
                        innerRadius: 50,
                        outerRadius: 75,
                        paddingAngle: 1,
                        highlightScope: { faded: "global", highlighted: "item" },
                      },
                    ]}
                    height={180}
                    width={180}
                    slotProps={{ legend: { hidden: true } }}
                  >
                    <PieCenterLabel
                      primaryText={String(dialogStatusDist.reduce((sum, d) => sum + d.value, 0))}
                      secondaryText={t("dashboard.topicHeader.total") || "Toplam"}
                    />
                  </PieChart>
                </Box>
              )}
              {/* Epoch Timeline Chart */}
              {dialogEpochData.labels.length > 0 && (
                <Box sx={{ flex: 1, minWidth: 220 }}>
                  <BarChart
                    skipAnimation
                    tooltip={{ trigger: "none" }}
                    axisHighlight={{ x: "none", y: "none" }}
                    xAxis={[
                      {
                        scaleType: "band",
                        data: dialogEpochData.labels,
                        tickLabelStyle: { fontSize: 11, angle: -45, textAnchor: "end" },
                      },
                    ]}
                    series={[
                      {
                        data: dialogEpochData.data,
                        color: dialogAlarmType === 1 ? "#f57c00" : "#c62828",
                      },
                    ]}
                    height={180}
                    margin={{ left: 40, right: 10, top: 10, bottom: 50 }}
                    slotProps={{ legend: { hidden: true } }}
                  />
                </Box>
              )}
            </Box>
          )}
          <List dense disablePadding>
            {dialogNodes.map((node, idx) => {
              let epochLabel = null;
              if (node.lastEpoch != null) {
                let ms = Number(node.lastEpoch);
                if (ms < 1e12) ms *= 1000;
                const d = new Date(ms);
                if (!isNaN(d.getTime())) {
                  epochLabel = d.toLocaleString(undefined, {
                    year: "2-digit",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  });
                }
              }
              return (
                <ListItemButton
                  key={idx}
                  onClick={() => navigate(`/places/${node.idNode}`)}
                  sx={{ py: 1 }}
                >
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: dialogAlarmType === 2 ? "#c62828" : "#f57c00",
                      mr: 1.5,
                      flexShrink: 0,
                    }}
                  />
                  <ListItemText
                    primary={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                        <span>{node.name}</span>
                        {epochLabel && (
                          <Chip
                            label={epochLabel}
                            size="small"
                            variant="outlined"
                            sx={{
                              height: 20,
                              fontSize: "0.7rem",
                              fontFamily:
                                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                            }}
                          />
                        )}
                      </Box>
                    }
                    sx={{ display: "flex", alignItems: "center" }}
                    primaryTypographyProps={{ component: "span" }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </DialogContent>
      </Dialog>

      {error && !loading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(255,255,255,0.75)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 14,
            color: "#b00020",
            fontWeight: 500,
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}
